import {
  CURRENCY_DEFINITIONS,
  CurrencyFieldConfig,
  CurrencyOption,
} from './currency.catalog';

export interface ParsedCurrencyAmount {
  numeric: boolean;
  amount: number | null;
}

export interface CurrencyAmountRules {
  required: boolean;
  min: number | null;
  max: number | null;
  decimalDigits: number;
}

const AMOUNT_PATTERN = /^(?:\d+\.?\d*|\.\d+)$/;

export const DEFAULT_CURRENCIES: readonly CurrencyOption[] = CURRENCY_DEFINITIONS;

/** Initial selection shown when no locked default currency is configured. */
export const CURRENCY_DISPLAY_FALLBACK = 'USD';

export const DEFAULT_CURRENCY_FIELD_CONFIG: CurrencyFieldConfig = {
  label: 'Amount',
  description: 'Enter the amount',
  placeholder: '0.00',
  defaultCurrency: null,
  allowedCurrencies: DEFAULT_CURRENCIES,
  decimalPrecision: null,
  required: false,
  allowChangeCurrency: true,
  disabled: false,
  readonly: false,
  min: null,
  max: null,
};

export function findCurrency(
  code: string | null | undefined,
  currencies: readonly CurrencyOption[],
): CurrencyOption | undefined {
  if (!code) {
    return undefined;
  }

  return currencies.find((currency) => currency.code === code);
}

export function currencyMatchesQuery(currency: CurrencyOption, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  return (
    currency.code.toLowerCase().includes(normalized) ||
    currency.name.toLowerCase().includes(normalized) ||
    currency.symbol.toLowerCase().includes(normalized) ||
    currency.flag.includes(normalized)
  );
}

export function parseCurrencyAmount(text: string): ParsedCurrencyAmount {
  const trimmed = text.trim();

  if (trimmed === '') {
    return { numeric: true, amount: null };
  }

  if (!AMOUNT_PATTERN.test(trimmed)) {
    return { numeric: false, amount: null };
  }

  const normalized = trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed;

  if (normalized === '' || normalized === '-' || normalized === '-.') {
    return { numeric: false, amount: null };
  }

  const amount = Number(normalized);

  if (!Number.isFinite(amount)) {
    return { numeric: false, amount: null };
  }

  return { numeric: true, amount };
}

export function countDecimalPlaces(text: string): number {
  const unsigned = text.trim().replace(/^-/, '');
  const dotIndex = unsigned.indexOf('.');

  if (dotIndex < 0) {
    return 0;
  }

  return unsigned.length - dotIndex - 1;
}

export function formatAmountForInput(amount: number | null | undefined): string {
  return amount == null ? '' : String(amount);
}

function trimmedStartsNegative(text: string): boolean {
  return text.trim().startsWith('-');
}

export function currencyAmountMessage(text: string, rules: CurrencyAmountRules): string | null {
  const parsed = parseCurrencyAmount(text);

  if (parsed.numeric && parsed.amount == null) {
    return rules.required ? 'Amount is required.' : null;
  }

  if (trimmedStartsNegative(text)) {
    return 'Enter a positive amount.';
  }

  if (!parsed.numeric || parsed.amount == null) {
    return 'Enter a valid number.';
  }

  const places = countDecimalPlaces(text);

  if (places > rules.decimalDigits) {
    if (rules.decimalDigits <= 0) {
      return 'Enter a whole number.';
    }

    const unit = rules.decimalDigits === 1 ? 'decimal place' : 'decimal places';
    return `Use at most ${rules.decimalDigits} ${unit}.`;
  }

  if (parsed.amount < 0) {
    return 'Enter a positive amount.';
  }

  if (rules.min != null && parsed.amount < rules.min) {
    return `Enter an amount of at least ${rules.min}.`;
  }

  if (rules.max != null && parsed.amount > rules.max) {
    return `Enter an amount no greater than ${rules.max}.`;
  }

  return null;
}

export function isAllowedAmountKey(event: KeyboardEvent, value: string): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return true;
  }

  const navigationKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];

  if (navigationKeys.includes(event.key)) {
    return true;
  }

  if (event.key === '-') {
    return false;
  }

  if (event.key === '.') {
    return !value.includes('.');
  }

  return /^\d$/.test(event.key);
}

export function getCurrencyShellClasses(disabled: boolean, invalid: boolean, focused: boolean): string {
  const layout =
    'relative flex h-11 w-full items-center overflow-hidden rounded-xl border transition-colors duration-200';

  if (disabled) {
    return `${layout} cursor-not-allowed border-gray-200 bg-gray-100`;
  }

  if (invalid) {
    const ring = focused ? ' ring-4 ring-red-100' : '';
    return `${layout} border-red-500 bg-white${ring}`;
  }

  if (focused) {
    return `${layout} border-blue-500 bg-white ring-4 ring-blue-100`;
  }

  return `${layout} border-gray-300 bg-white hover:border-blue-400`;
}
